/**
 * Enforces Shopify's variant.quantity_rule (min/max/increment) on any
 * <input data-b2b-quantity-rule> so /cart/add can't submit an invalid quantity.
 */
;(() => {
  const PLACEHOLDER = "[count]"
  const FIELD_SELECTOR = "[data-b2b-quantity-rule]"

  const strings = () =>
    document.querySelector("[data-qty-note-strings]")?.dataset || {}

  const setNote = (input, key, count) => {
    const id = input.getAttribute("aria-describedby")
    const note = id ? document.getElementById(id) : null
    if (!note) {
      return
    }
    const template = key ? strings()[key] : ""
    note.textContent = template
      ? template.split(PLACEHOLDER).join(String(count))
      : ""
    note.hidden = !note.textContent
    // The cart marks its own failures on this shared element; taking the note
    // over drops that mark, or the cart later clears a rule note as its own.
    delete note.dataset.kind
  }

  const numberOr = (raw, fallback) => {
    const value = Number(raw)
    return raw !== "" && raw != null && Number.isFinite(value)
      ? value
      : fallback
  }

  /** Valid quantity for this field plus the rule that bound it. Pure. */
  const evaluate = (input) => {
    // `data-qty-step` carries the increment where native `step` can't: native
    // validation measures from `min` (0 here) and would reject valid quantities.
    const step = Number(input.dataset.qtyStep) || Number(input.step) || 1

    // Field floor: the PDP matrix uses min="0" as a "not ordering this row"
    // sentinel, while `data-qty-min` always carries the rule's real minimum.
    const floor = numberOr(input.min, 1)
    const ruleMin = numberOr(input.dataset.qtyMin, floor)
    const ruleMax = numberOr(input.dataset.qtyMax, null)
    const stockMax = numberOr(input.dataset.qtyStockMax, null)

    let max = ruleMax
    let maxKey = ruleMax === null ? null : "ruleMaxText"
    if (stockMax !== null && (max === null || stockMax < max)) {
      max = stockMax
      maxKey = "stockText"
    }

    let value = Math.floor(Number(input.value))
    let noteKey = null
    let noteCount = 0

    if (!Number.isFinite(value) || value <= floor) {
      // Blank / non-numeric / at-or-below the floor collapses to the floor,
      // which is the 0 sentinel for the matrix and the rule min for quick-order.
      return { value: floor, noteKey, noteCount }
    }

    if (max !== null && max < ruleMin) {
      // No quantity satisfies both rules, so report the row unavailable rather
      // than clamp to a minimum the ceiling would make Shopify reject.
      return { value: floor, noteKey: "unavailableText", noteCount: max }
    }

    // Nonzero orders must satisfy the variant quantity rule.
    if (value < ruleMin) {
      value = ruleMin
      noteKey = "ruleMinText"
      noteCount = ruleMin
    }

    // Round to the nearest increment, measured from the rule min.
    if (step > 1) {
      const rounded = ruleMin + Math.round((value - ruleMin) / step) * step
      if (rounded !== value) {
        noteKey = "stepText"
        noteCount = step
      }
      value = rounded
    }

    if (max !== null && value > max) {
      // Largest valid increment that does not exceed max.
      value = ruleMin + Math.max(0, Math.floor((max - ruleMin) / step)) * step
      // A ceiling outranks a rounding note: it is why the buyer cannot have the
      // number they typed, and it is the one they need to read.
      noteKey = maxKey
      noteCount = value
    }

    return { value, noteKey, noteCount }
  }

  /** Corrects the field and explains the correction. */
  const clamp = (input) => {
    const { value, noteKey, noteCount } = evaluate(input)
    setNote(input, noteKey, noteCount)

    // Re-dispatch `input` so listeners bound on inner scopes (the product form's
    // live totals) recompute from the clamped value instead of the raw entry.
    const next = String(value)
    if (input.value !== next) {
      input.value = next
      input.dispatchEvent(new Event("input", { bubbles: true }))
    }
    return value
  }

  /**
   * Explains without correcting, on purpose: rewriting a saved cart line on
   * load would change the buyer's order behind their back, so only mark it.
   */
  const check = (input) => {
    const { value, noteKey, noteCount } = evaluate(input)
    setNote(
      input,
      value === Math.floor(Number(input.value)) ? null : noteKey,
      noteCount
    )
  }

  const checkAll = (root = document) => {
    for (const input of root.querySelectorAll(FIELD_SELECTOR)) {
      check(input)
    }
  }

  document.addEventListener("change", (event) => {
    const target = event.target
    if (target && target.matches && target.matches(FIELD_SELECTOR)) {
      clamp(target)
    }
  })

  // Other modules write quantities programmatically (the PDP hydrates its matrix
  // from the cart), which fires no `change`, so they call clamp themselves.
  window.B2BQuantityRules = { clamp, check, checkAll }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => checkAll())
  } else {
    checkAll()
  }
})()
